import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../../../../contexts/AuthContext'
import CommunityService from '../../../../service/CommunityService'
import CreateThreadModal from '../components/CreateThreadModal/CreateThreadModal'
import styles from '../CommunityPage.module.scss'
import { CommentIcon, EyeIcon, LockIcon, PinIcon, ForumIcon } from '../../../../components/CustomIcons'

const formatDate = (dateStr) => {
  if (!dateStr) return 'Недавно'
  const s = String(dateStr)
  // Handle DD.MM.YYYY HH:MM (DRF DATETIME_FORMAT)
  const m = s.match(/^(\d{2})\.(\d{2})\.(\d{4})\s+(\d{2}):(\d{2})/)
  if (m) {
    const d = new Date(`${m[3]}-${m[2]}-${m[1]}T${m[4]}:${m[5]}`)
    if (!isNaN(d.getTime())) return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }
  const d = new Date(s.replace(' ', 'T'))
  if (isNaN(d.getTime())) return 'Недавно'
  return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export default function ForumPage() {
  const { forumId } = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const [forum, setForum] = useState(null)
  const [threads, setThreads] = useState([])
  const [loading, setLoading] = useState(true)
  const [showCreateThread, setShowCreateThread] = useState(false)

  useEffect(() => {
    loadForum()
  }, [forumId])

  const loadForum = async () => {
    try {
      const [forumRes, threadsRes] = await Promise.all([
        CommunityService.getForum(forumId),
        CommunityService.getForumThreads(forumId)
      ])
      setForum(forumRes.data)
      setThreads(threadsRes.data?.results || threadsRes.data || [])
    } catch (e) {
      console.error('Error loading forum', e)
    } finally {
      setLoading(false)
    }
  }

  const handleCreateThread = async (threadData) => {
    await CommunityService.createForumThread(forumId, threadData)
    setShowCreateThread(false)
    const r = await CommunityService.getForumThreads(forumId)
    setThreads(r.data?.results || r.data || [])
  }

  if (loading) {
    return (
      <div className={styles.communityPage}>
        <div className={styles.loading}>Загрузка форума...</div>
      </div>
    )
  }

  return (
    <div className={styles.communityPage}>
      <div className={styles.forumView} data-lenis-prevent>
        <div className={styles.forumViewHeader}>
          <button
            className={styles.backButton}
            onClick={() => navigate(-1)}
          >
            ← Назад к форумам
          </button>
          <div className={styles.forumInfo}>
            <h3><ForumIcon size={24} style={{ marginRight: '8px' }} />{forum?.name}</h3>
            <p>{forum?.description}</p>
          </div>
          {isAuthenticated && (
            <button
              className={styles.createButton}
              onClick={() => setShowCreateThread(true)}
            >
              📝 Создать тему
            </button>
          )}
        </div>

        <div className={styles.threadsList}>
          {threads.length === 0 ? (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>📄</div>
              <h3>Тем не найдено</h3>
              <p>В этом форуме пока нет тем для обсуждения</p>
              {isAuthenticated && (
                <button
                  className={styles.createButton}
                  onClick={() => setShowCreateThread(true)}
                >
                  📝 Создать первую тему
                </button>
              )}
            </div>
          ) : (
            threads.map((thread) => {
              const authorAvatar = thread.user?.avatar
                ? (thread.user.avatar.startsWith('http')
                    ? thread.user.avatar
                    : `https://security.ncore.kg${thread.user.avatar}`)
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(thread.user?.username || 'User')}&background=E91E63&color=fff&bold=true`

              return (
                <div
                  key={thread.id}
                  className={`${styles.threadCard} ${thread.is_pinned ? styles.pinned : ''}`}
                  onClick={() => navigate(`/services/community/forum/${forumId}/thread/${thread.id}`)}
                >
                  <div className={styles.threadHeader}>
                    <div className={styles.threadTitleSection}>
                      <h4>{thread.title}</h4>
                      <div className={styles.threadBadges}>
                        {thread.is_pinned && <span className={styles.pinnedBadge}><PinIcon size={14} style={{ marginRight: '4px' }} /> Закреплено</span>}
                        {thread.is_locked && <span className={styles.lockedBadge}><LockIcon size={14} style={{ marginRight: '4px' }} /> Заблокировано</span>}
                        {thread.is_solved && <span className={styles.solvedBadge}>✓ Решено</span>}
                      </div>
                    </div>
                  </div>
                  <div className={styles.threadMeta}>
                    <div className={styles.threadAuthor}>
                      <img
                        src={authorAvatar}
                        alt={thread.user?.username}
                        className={styles.threadAuthorAvatar}
                        onError={(e) => {
                          e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(thread.user?.username || 'User')}&background=E91E63&color=fff&bold=true`
                        }}
                      />
                      <span className={styles.threadAuthorName}>{thread.user?.username || 'Неизвестно'}</span>
                    </div>
                    <span><CommentIcon size={14} style={{ verticalAlign: 'middle', marginRight: '4px' }} />{thread.posts_count || 0} ответов</span>
                    <span>🕒 {formatDate(thread.created_at)}</span>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {showCreateThread && (
        <CreateThreadModal
          isOpen={showCreateThread}
          onClose={() => setShowCreateThread(false)}
          onSubmit={handleCreateThread}
          forumName={forum?.name}
        />
      )}
    </div>
  )
}
